import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Eye, EyeOff, Copy, Lock, Trash2 } from "lucide-react";
import { toast } from "sonner";

const categories = ["GST", "Income Tax", "MCA", "ICEGATE", "DGFT", "Professional Tax", "PF", "ESI", "TRACES", "Bank", "DSC", "Other"];
const visibilities = ["owner", "partner", "all"];

export default function Vault() {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [reveal, setReveal] = useState({}); // id -> revealed obj
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    label: "", category: "GST", client_id: "", username: "", password: "", url: "", remarks: "", visibility: "owner", pin: "",
  });

  async function load() { const { data } = await api.get("/vault"); setRows(data); }
  useEffect(() => { load(); api.get("/clients").then(({ data }) => setClients(data)); }, []);

  async function submit(e) {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.client_id) delete payload.client_id;
      await api.post("/vault", payload);
      toast.success("Credential stored securely");
      setOpen(false);
      setForm({ label: "", category: "GST", client_id: "", username: "", password: "", url: "", remarks: "", visibility: "owner", pin: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  }

  async function doReveal(id) {
    if (reveal[id]) { setReveal(r => { const n = { ...r }; delete n[id]; return n; }); return; }
    const { data } = await api.get(`/vault/${id}/reveal`);
    setReveal(r => ({ ...r, [id]: data }));
    setTimeout(() => setReveal(r => { const n = { ...r }; delete n[id]; return n; }), 30000);
  }

  async function del(id) {
    if (!confirm("Delete this credential?")) return;
    await api.delete(`/vault/${id}`);
    toast.success("Deleted");
    load();
  }

  const clientMap = Object.fromEntries(clients.map(c => [c.id, c.client_name]));

  return (
    <div className="space-y-5" data-testid="vault-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
            <Lock className="w-3 h-3" /> AES-256 encrypted
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Credential Vault</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-credential-btn" className="bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-1.5" />Add Credential</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Credential</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Label *</Label><Input data-testid="cred-label-input" required value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Category</Label>
                  <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Client</Label>
                  <Select value={form.client_id} onValueChange={v => setForm({ ...form, client_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                    <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.client_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>URL</Label><Input value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} /></div>
              <div><Label>Username</Label><Input data-testid="cred-username-input" className="font-mono" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} /></div>
              <div><Label>Password</Label><Input data-testid="cred-password-input" type="password" className="font-mono" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} /></div>
              <div><Label>PIN</Label><Input type="password" className="font-mono" value={form.pin} onChange={e => setForm({ ...form, pin: e.target.value })} /></div>
              <div><Label>Visibility</Label>
                <Select value={form.visibility} onValueChange={v => setForm({ ...form, visibility: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{visibilities.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <DialogFooter><Button data-testid="save-credential-btn" type="submit" className="bg-blue-600 hover:bg-blue-700">Save (encrypted)</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {rows.length === 0 && (
          <Card className="p-10 col-span-full text-center">
            <Lock className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
            <div className="text-sm text-muted-foreground">No credentials yet. Add GST/IT/MCA logins securely.</div>
          </Card>
        )}
        {rows.map(v => {
          const r = reveal[v.id];
          return (
            <Card key={v.id} className="p-4 border-l-[3px] border-blue-600" data-testid={`vault-card-${v.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-widest">{v.category}</div>
                  <div className="font-display font-semibold mt-1">{v.label}</div>
                  {v.client_id && <div className="text-[10px] text-muted-foreground">{clientMap[v.client_id]}</div>}
                </div>
                <Badge variant="outline" className="text-[10px]">{v.visibility}</Badge>
              </div>

              <div className="mt-4 space-y-2 text-xs">
                <div className="flex items-center gap-2">
                  <span className="w-20 text-muted-foreground uppercase tracking-widest text-[10px]">User</span>
                  <span className="font-mono flex-1 truncate">{v.username || "—"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-20 text-muted-foreground uppercase tracking-widest text-[10px]">Pass</span>
                  <span className="font-mono flex-1 truncate">{r ? r.password : "••••••••••"}</span>
                  <button data-testid={`reveal-btn-${v.id}`} onClick={() => doReveal(v.id)} className="text-muted-foreground hover:text-blue-600">
                    {r ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                  {r && (
                    <button onClick={() => { navigator.clipboard.writeText(r.password); toast.success("Copied"); }} className="text-muted-foreground hover:text-blue-600">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {v.url && (
                  <div className="flex items-center gap-2">
                    <span className="w-20 text-muted-foreground uppercase tracking-widest text-[10px]">URL</span>
                    <a href={v.url} target="_blank" rel="noreferrer" className="font-mono text-blue-600 truncate hover:underline">{v.url}</a>
                  </div>
                )}
              </div>

              <div className="mt-3 pt-3 border-t border-border flex justify-end">
                <Button size="sm" variant="ghost" onClick={() => del(v.id)} className="h-7 text-[11px] text-red-600">
                  <Trash2 className="w-3 h-3 mr-1" /> Delete
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
